import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';

import { PackagesSelectionModal } from '@/lazy-load-components';

import { CollectionType } from '@/enums/collection-type';
import { ProviderFilterPlace } from '@/enums/provider-filter-place';

import { ModalHelper } from '@/helpers/modal-helper-vfm';
import { calculateUrl } from '@/helpers/state-helper';

import Provider from '@/interfaces/provider';

import { FilterCollection } from '@/stores/filter.store';
import { useConstantStore, useFilterStore, useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { useRouter } from 'vue-router/composables';
import ResetButton from '@/components/buttons/ResetButton.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';

type FilterBarPopularProviderProps = {
	addButtonVisible?: boolean;
	showAddButtonFirst?: boolean;
	collectionType: CollectionType;
	currentFilters: FilterCollection;
	providersClickable?: boolean;
	userProviders?: Provider[];
	updateRouteState?: boolean; // when toggling providers
	trackEvent?: boolean; // when toggling providers
	userSelectedPackages?: string[]; // used for sports provider until we have a better flagging mechanism in place
};


export default /*#__PURE__*/_defineComponent({
  __name: 'FilterBarPopularProvider',
  props: {
    addButtonVisible: { type: Boolean, default: true },
    showAddButtonFirst: { type: Boolean },
    collectionType: null,
    currentFilters: null,
    providersClickable: { type: Boolean, default: true },
    userProviders: { default: () => [] },
    updateRouteState: { type: Boolean, default: true },
    trackEvent: { type: Boolean, default: true },
    userSelectedPackages: { default: () => [] }
  },
  emits: ["toggle"],
  setup(__props: any, { emit: emits }: { emit: ({
	(
		e: 'toggle',
		toggleData: {
			filterName: string;
			value: string;
			selectedState: 0 | 1;
			updateRouteState: boolean;
			userSelectedPackages: string[];
			trackEvent: boolean;
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { addButtonVisible: boolean, showAddButtonFirst?: boolean, collectionType: CollectionType, currentFilters: FilterCollection, providersClickable: boolean, userProviders: Provider[], updateRouteState: boolean, trackEvent: boolean, userSelectedPackages: string[] };





const {
	toggle,
}: {
	toggle: (payload: {
		filterName: keyof FilterCollection;
		value: any;
		selectedState: 1 | 0;
		updateRouteState?: boolean;
		userSelectedPackages?: string[];
		trackEvent?: boolean;
	}) => void;
} = useFilterStore();

const { providersById, providersByShortName, genresByShortName } = useConstantStore();
const { settings } = useUserStore();
const { language } = useLanguageStore();

const router = useRouter();
const isModalOpening = ref(false);

const selectedProvider = computed(() => {
	return props.userProviders.find(el => el.shortName === props.currentFilters.providers?.[0]);
});

const IMG_PROVIDERS_BAR_LOAD_COUNT = computed(() => {
	return global.IMG_PROVIDERS_BAR_LOAD_COUNT;
});

const plusIcon = computed(() => {
	return `/${ASSETS_DIR}/img/plus_icon.png`;
});

const plusIconIsVisible = computed(() => {
	// causing issue with SSR > client hydration

	if (props.addButtonVisible) {
		return settings?.value.providers?.length > 0;
	}
	return props.addButtonVisible;
});

const providerUrls = ref<Record<string, string>>({});

const _providersById = ref(providersById.value);

// SSR perf: only call providersById once instead of every v-for loop.
watch(
	() => props.userProviders,
	() => {
		_providersById.value = providersById.value;
	},
	{ immediate: true }
);

watch(
	language,
	() => {
		// every call to a vuex getter seems to cost performance, that's why we only call it once before the loop
		const _providersByShortName = providersByShortName.value;
		const _genresByShortName = genresByShortName.value;
		// cached getters don't work in SSR, that's why we optimize it by putting it into data
		// disable subgenre parametric urls for better interlinking
		const { subgenres, ...filters } = props.currentFilters;
		void subgenres;

		providerUrls.value = props.userProviders.reduce((root, provider: Provider) => {
			root[provider.shortName] = calculateUrl(
				router,
				undefined,
				props.collectionType,
				{
					...filters,
					providers: [provider.shortName],
				},
				_providersByShortName,
				_genresByShortName
			);
			return root;
		}, {} as Record<string, string>);
	},
	{ immediate: true }
);

function providerClicked(providerName: string) {
	if (props.providersClickable) {
		const isActive: 1 | 0 = isFiltersContainsProvider(providerName) ? 0 : 1;
		const toggleData = {
			filterName: 'providers',
			value: providerName,
			selectedState: isActive,
			updateRouteState: props.updateRouteState,
			userSelectedPackages: props.userSelectedPackages,
			trackEvent: props.trackEvent,
		};

		toggle(toggleData as any);
		emits('toggle', toggleData);
	}
}
function isFiltersContainsProvider(providerName: string): boolean {
	return props.currentFilters.providers.includes(providerName);
}
function isProviderActive(providerName: string): boolean {
	return props.currentFilters.providers.length === 0 || isFiltersContainsProvider(providerName);
}

function onWillDismiss() {
	isModalOpening.value = false;
}

async function openProviderSelection() {
	isModalOpening.value = true;

	await ModalHelper.openModal(
		PackagesSelectionModal,
		{ providerPlace: ProviderFilterPlace.SETTINGS, onWillDismiss },
		{ cssClass: 'packages-selection-modal' },
		ProviderFilterPlace.SETTINGS,
		true
	);
	isModalOpening.value = false;
}

return { __sfc: true,props, emits, toggle, providersById, providersByShortName, genresByShortName, settings, language, router, isModalOpening, selectedProvider, IMG_PROVIDERS_BAR_LOAD_COUNT, plusIcon, plusIconIsVisible, providerUrls, _providersById, providerClicked, isFiltersContainsProvider, isProviderActive, onWillDismiss, openProviderSelection, ResetButton, ProviderIcon, HiddenHorizontalScrollbar }
}

})